/**
 * The two reading screens — a chapter's opener and a level's debrief —
 * and the contents rail they share (components/opener/*, components/debrief/*).
 *
 * See web/src/lib/strings/index.ts for how this file is shaped and why, and
 * docs/i18n.md for the Arabic glossary every value here must follow. Neither
 * object may be `as const`: `ar` is typed against `en`, and literal types
 * would make it have to repeat the English words.
 *
 * ## What is *not* here
 *
 * The prose itself. An essay and an article are authored content, translated
 * beside their originals (`missions/_worlds/NN-slug.ar.md`,
 * `missions/lNN-slug/article.ar.md`) and fetched whole in the language the
 * reader is in — see lib/openers.ts and lib/articles.ts. This file is only
 * the furniture around them: the eyebrow, the estimate, the rail, the two
 * footers and the seven states in which there is no prose to show.
 *
 * ## The arrow
 *
 * `debrief.back` carries a glyph that points, and it points the other way in
 * Arabic. It is in the catalogue rather than in the component, because "which
 * way is back" is a fact about a language and not about a link — the same
 * reason `←` is not a CSS `transform` here: it is a word in this sentence.
 *
 * ## No `<Parts>` in this file
 *
 * Every sentence on these two screens is either plain or takes a number and a
 * name; none carries an element mid-clause. If one ever does — a star, a
 * piece of bold — it becomes an array of parts rendered by `<Parts of={…} />`
 * rather than three strings joined in the component (strings/index.ts).
 */
import { ltr, num } from '../format';

export const en = {
  /* --- Both screens ------------------------------------------------------ */

  /** The rail's heading, and its `aria-label` as a landmark. */
  contents: 'Contents',
  /** First in both top bars, and the way out of a state with nothing to read. */
  levelList: 'Level list',
  tryAgain: 'Try again',
  /**
   * The estimate above both kinds of article. The figure is worked out in
   * words per minute at a pace that differs by language — an opener's comes
   * from the build, a debrief's from the component (components/debrief/
   * Debrief.tsx) — and this only says it.
   */
  readingTime: (minutes: number) => `${num(minutes, 'en')} min read`,

  /* --- The chapter opener ------------------------------------------------ */

  opener: {
    skipToLevel: (level: number) => `Skip to level ${num(level, 'en')}`,
    beforeLevel: (level: number) => `Before level ${num(level, 'en')}`,
    /** The chip on the head: how much chapter this is, and where it sits. */
    span: (levels: number, first: number, last: number) =>
      `${num(levels, 'en')} ${levels === 1 ? 'level' : 'levels'} · ${num(first, 'en')}–${num(last, 'en')}`,
    /* Said out loud, because the commonest reason a learner skips a page like
       this is the suspicion that it is homework with a test at the end. */
    optional: 'Nothing here is needed to pass the level',
    opening: 'Opening…',
    footNote: 'That is the why. The rest of this chapter is the how.',
    start: (level: number, title: string) => `Start level ${num(level, 'en')} · ${title}`,
    sandbox: 'Practise without grading (level access required)',

    /* The two ways there is no essay here. Neither is a locked door — an
       opener is never withheld from anybody — so neither offers a price. */
    missing: {
      title: 'Not written yet.',
      body: 'This chapter does not have its introduction yet. The levels themselves are unaffected — each one still opens with the tools it needs.',
    },
    unavailable: {
      title: 'Could not load it.',
      body: 'This page could not reach the chapter introduction. Your progress is untouched.',
    },
  },

  /* --- The level debrief -------------------------------------------------- */

  debrief: {
    replay: 'Replay level',
    level: (level: number) => `Level ${num(level, 'en')}`,
    opening: 'Opening the debrief…',
    /** The glyph points at the earlier reading, which is behind you. */
    back: (level: number, title: string) => `← Level ${num(level, 'en')} · ${title}`,
    next: (level: number) => `Next · Level ${num(level, 'en')}`,
    allCleared: 'Every level cleared',
    /** One button, two gates: not signed in, and signed in without the course. */
    getCourse: 'Get the course',

    /**
     * The five states that are not an article. Each says the true thing and
     * offers the one action that changes it — never a disabled button and
     * never a shrug. The middle two are the only place in the product that
     * asks anybody for money, so they are written as an argument rather than
     * as an error: what is behind this, and why it is worth having.
     */
    unplayed: {
      title: 'Play it first.',
      body: (level: number) =>
        `The debrief explains every command this level uses and why each one was the right one, which means it is the answer written out at length. It is worth more after the level than before it — so it opens once level ${num(level, 'en')} is cleared.`,
      cta: (level: number) => `Play level ${num(level, 'en')}`,
    },
    anonymous: {
      title: 'Sign in to read it.',
      body: 'This level is part of the paid half of the course, and so is its debrief. It is served against your account rather than to the whole internet, so there is nothing to read here until you are signed in.',
    },
    unowned: {
      title: 'This one is in the paid half.',
      /**
       * `from` and `count` are the paid half as the catalogue reports it
       * (lib/entitlement's `paidSpan`), not the two numerals this sentence
       * used to carry. It said "from 20 on" and "thirty levels", and the
       * second of those went quietly wrong the day the fifty-first and
       * fifty-second levels shipped — in both languages, on a page that is
       * one of the three places in the product that asks anybody for money.
       * A count belongs to the course, not to the copy.
       *
       * `from` is where the paid half of *this level's lane* starts, and the
       * sentence is only allowed it when the catalogue can say so
       * (`paidSpan`'s `range`, lib/entitlement.ts). A wholly paid
       * specialization track has no such boundary — it is paid from its
       * first level, and its first level is numbered zero like every other
       * track's — so `bodyCount` below says the same thing without pointing
       * at a level number that belongs to another lane.
       */
      body: (signedIn: boolean, from: number, count: number) =>
        `Every level from ${num(from, 'en')} on comes with a debrief like this one — the commands it used, why each of them was the right one, and the part of the job it comes from. ${
          signedIn
            ? 'Your account does not have the course yet.'
            : `That is ${num(count, 'en')} ${count === 1 ? 'level' : 'levels'}, and ${num(count, 'en')} of these.`
        }`,
      bodyCount: (signedIn: boolean, count: number) =>
        `Every level in the paid half comes with a debrief like this one — the commands it used, why each of them was the right one, and the part of the job it comes from. ${
          signedIn
            ? 'Your account does not have the course yet.'
            : `That is ${num(count, 'en')} ${count === 1 ? 'level' : 'levels'}, and ${num(count, 'en')} of these.`
        }`,
    },
    missing: {
      title: 'Not written yet.',
      body: 'This level does not have its debrief yet. The reflection at the end of the level is the short version in the meantime.',
    },
    unavailable: {
      title: 'Could not load it.',
      body: 'The debrief lives on the server and this page could not reach it. Your progress is untouched.',
    },
  },
};

/**
 * Arabic counts its nouns, and this is the reason a sentence with a number in
 * it is a function.
 *
 * 3 to 10 take the plural (`5 دقائق`); 11 and up take the singular
 * (`15 دقيقة`). One and two take their own forms: a chapter is never one
 * level long, and an essay is never one minute, so the singular covers them —
 * `1 مستوى` is the form Arabic uses when the numeral is written out anyway.
 */
const plural = (n: number, few: string, one: string) => (n >= 3 && n <= 10 ? few : one);

export const ar: typeof en = {
  contents: 'المحتويات',
  levelList: 'قائمة المستويات',
  tryAgain: 'أعد المحاولة',
  readingTime: (minutes: number) => `${num(minutes, 'ar')} ${plural(minutes, 'دقائق', 'دقيقة')} للقراءة`,

  opener: {
    skipToLevel: (level: number) => `تخطَّ إلى المستوى ${num(level, 'ar')}`,
    beforeLevel: (level: number) => `قبل المستوى ${num(level, 'ar')}`,
    /* The range is isolated: an en dash between two numerals is a neutral
       character, and inside a right-to-left run the two numbers come out
       either side of it in the wrong order — `2–6` read as `6–2`. */
    span: (levels: number, first: number, last: number) =>
      `${num(levels, 'ar')} ${plural(levels, 'مستويات', 'مستوى')} · ${ltr(`${num(first, 'ar')}–${num(last, 'ar')}`)}`,
    optional: 'هذه المقدمة اختيارية ولا تؤثر في اجتياز المستوى',
    opening: 'جارٍ الفتح…',
    footNote: 'تلك هي الفكرة. وستتعلم التطبيق في بقية الفصل.',
    start: (level: number, title: string) => `ابدأ المستوى ${num(level, 'ar')} · ${title}`,
    sandbox: 'تدرّب بلا تقييم (يتطلب إتاحة المستوى)',

    missing: {
      title: 'المقدمة غير متاحة بعد.',
      body: 'لم تُكتب مقدمة هذا الفصل بعد. يمكنك متابعة المستويات كالمعتاد؛ يبدأ كل مستوى بالأدوات التي يحتاجها.',
    },
    unavailable: {
      title: 'تعذّر تحميل المقدمة.',
      body: 'لم تتمكن الصفحة من تحميل مقدمة الفصل. لن يؤثر ذلك في تقدمك.',
    },
  },

  debrief: {
    replay: 'أعد لعب المستوى',
    level: (level: number) => `المستوى ${num(level, 'ar')}`,
    opening: 'جارٍ فتح الخلاصة…',
    /* Back is the other way in Arabic: the earlier reading is to the right,
       because that is where the line starts. */
    back: (level: number, title: string) => `→ المستوى ${num(level, 'ar')} · ${title}`,
    next: (level: number) => `التالي · المستوى ${num(level, 'ar')}`,
    allCleared: 'أنجزت كل المستويات',
    getCourse: 'احصل على الدورة',

    unplayed: {
      title: 'أنجز المستوى أولًا.',
      body: (level: number) =>
        `تشرح الخلاصة كل أمر في هذا المستوى ولماذا كان مناسبًا، أي أنها تتضمن الحل كاملًا. ستستفيد منها أكثر بعد المحاولة، لذلك تُفتح فور إنجاز المستوى ${num(level, 'ar')}.`,
      cta: (level: number) => `ابدأ المستوى ${num(level, 'ar')}`,
    },
    anonymous: {
      title: 'سجّل الدخول لقراءتها.',
      body: 'هذا المستوى وخلاصته ضمن الدورة الكاملة. سجّل الدخول أولًا لنتحقق من أن الدورة متاحة في حسابك.',
    },
    unowned: {
      title: 'هذا المستوى ضمن الدورة الكاملة.',
      body: (signedIn: boolean, from: number, count: number) =>
        `يأتي كل مستوى من المستوى ${num(from, 'ar')} فصاعدًا بخلاصة تشرح الأوامر المستخدمة، وسبب اختيارها، وسياقها في العمل الحقيقي. ${
          signedIn
            ? 'الدورة الكاملة غير مضافة إلى حسابك بعد.'
            : `سجّل الدخول لفتح ${num(count, 'ar')} ${plural(count, 'مستويات', 'مستوى')} وخلاصاتها.`
        }`,
      /* `في النصف المدفوع` in place of `من المستوى كذا فصاعدًا`: when there is
         no boundary to name, the paid half is named as itself rather than by
         a number that would belong to whichever lane the reader is not in.
         The second half of the sentence is unchanged — it was already
         counting. */
      bodyCount: (signedIn: boolean, count: number) =>
        `يأتي كل مستوى في النصف المدفوع بخلاصة تشرح الأوامر المستخدمة، وسبب اختيارها، وسياقها في العمل الحقيقي. ${
          signedIn
            ? 'الدورة الكاملة غير مضافة إلى حسابك بعد.'
            : `سجّل الدخول لفتح ${num(count, 'ar')} ${plural(count, 'مستويات', 'مستوى')} وخلاصاتها.`
        }`,
    },
    missing: {
      title: 'الخلاصة غير متاحة بعد.',
      body: 'لم تُكتب خلاصة هذا المستوى بعد. يمكنك حاليًا الرجوع إلى الملخص القصير الذي ظهر عند إنهائه.',
    },
    unavailable: {
      title: 'تعذّر تحميل الخلاصة.',
      body: 'لم تتمكن الصفحة من تحميل الخلاصة من الخادم. لن يؤثر ذلك في تقدمك.',
    },
  },
};

const levelsDe = (n: number) => `${num(n, 'de')} Level`;

export const de: typeof en = {
  contents: 'Inhalt',
  levelList: 'Levelliste',
  tryAgain: 'Erneut versuchen',
  readingTime: (minutes) => `${num(minutes, 'de')} Min. Lesezeit`,
  opener: {
    skipToLevel: (level) => `Weiter zu Level ${num(level, 'de')}`,
    beforeLevel: (level) => `Vor Level ${num(level, 'de')}`,
    span: (levels, first, last) => `${levelsDe(levels)} · ${num(first, 'de')}–${num(last, 'de')}`,
    optional: 'Nichts hiervon brauchst du, um das Level zu bestehen',
    opening: 'Wird geöffnet …',
    footNote: 'Das ist das Warum. Der Rest dieses Kapitels ist das Wie.',
    start: (level, title) => `Level ${num(level, 'de')} starten · ${title}`,
    sandbox: 'Ohne Bewertung üben (Levelzugang erforderlich)',
    missing: {
      title: 'Noch nicht geschrieben.',
      body: 'Dieses Kapitel hat noch keine Einführung. Die Level selbst sind davon nicht betroffen – jedes beginnt weiterhin mit den Werkzeugen, die es braucht.',
    },
    unavailable: {
      title: 'Konnte nicht geladen werden.',
      body: 'Diese Seite hat die Einführung ins Kapitel nicht erreicht. Dein Fortschritt ist unverändert.',
    },
  },
  debrief: {
    replay: 'Level wiederholen',
    level: (level) => `Level ${num(level, 'de')}`,
    opening: 'Die Nachbesprechung wird geöffnet …',
    back: (level, title) => `← Level ${num(level, 'de')} · ${title}`,
    next: (level) => `Weiter · Level ${num(level, 'de')}`,
    allCleared: 'Alle Level geschafft',
    getCourse: 'Kurs holen',
    unplayed: {
      title: 'Spiel es zuerst.',
      body: (level) =>
        `Die Nachbesprechung erklärt jeden Befehl, den dieses Level benutzt, und warum er jeweils der richtige war – sie ist also die ausführlich aufgeschriebene Lösung. Nach dem Level ist sie mehr wert als davor, deshalb öffnet sie sich, sobald Level ${num(level, 'de')} geschafft ist.`,
      cta: (level) => `Level ${num(level, 'de')} spielen`,
    },
    anonymous: {
      title: 'Melde dich an, um sie zu lesen.',
      body: 'Dieses Level gehört zum bezahlten Teil des Kurses, und seine Nachbesprechung auch. Sie wird für dein Konto ausgeliefert, nicht fürs ganze Internet – bis du angemeldet bist, gibt es hier also nichts zu lesen.',
    },
    unowned: {
      title: 'Diese gehört zum bezahlten Teil.',
      body: (signedIn, from, count) =>
        `Jedes Level ab ${num(from, 'de')} hat eine Nachbesprechung wie diese – die Befehle, die es benutzt hat, warum jeder davon der richtige war und aus welchem Teil des Jobs es stammt. ${signedIn ? 'Dein Konto hat den Kurs noch nicht.' : `Das sind ${levelsDe(count)} und ${num(count, 'de')} solche Nachbesprechungen.`}`,
      bodyCount: (signedIn, count) =>
        `Jedes Level im bezahlten Teil hat eine Nachbesprechung wie diese – die Befehle, die es benutzt hat, warum jeder davon der richtige war und aus welchem Teil des Jobs es stammt. ${signedIn ? 'Dein Konto hat den Kurs noch nicht.' : `Das sind ${levelsDe(count)} und ${num(count, 'de')} solche Nachbesprechungen.`}`,
    },
    missing: {
      title: 'Noch nicht geschrieben.',
      body: 'Dieses Level hat noch keine Nachbesprechung. Bis dahin ist die Reflexion am Ende des Levels die Kurzfassung.',
    },
    unavailable: {
      title: 'Konnte nicht geladen werden.',
      body: 'Die Nachbesprechung liegt auf dem Server, und diese Seite hat ihn nicht erreicht. Dein Fortschritt ist unverändert.',
    },
  },
};

export const ja: typeof en = {
  contents: '目次',
  levelList: 'レベル一覧',
  tryAgain: 'もう一度試す',
  readingTime: (minutes: number) => `読了まで約${num(minutes, 'ja')}分`,
  opener: {
    skipToLevel: (level: number) => `レベル${num(level, 'ja')}へスキップ`,
    beforeLevel: (level: number) => `レベル${num(level, 'ja')}の前に`,
    span: (levels: number, first: number, last: number) =>
      `${num(levels, 'ja')}レベル · ${num(first, 'ja')}〜${num(last, 'ja')}`,
    optional: 'ここに書かれていることは、レベルをクリアするために必須ではありません',
    opening: '開いています…',
    footNote: 'ここまでが「なぜ」です。この章の残りは「どうやるか」です。',
    start: (level: number, title: string) => `レベル${num(level, 'ja')}を開始 · ${title}`,
    sandbox: '採点なしで練習する（レベルへのアクセスが必要）',
    missing: {
      title: 'まだ書かれていません。',
      body: 'この章の導入はまだありません。各レベルには影響しません。どのレベルも、必要なツールを備えた状態で始まります。',
    },
    unavailable: {
      title: '読み込めませんでした。',
      body: 'このページから章の導入に接続できませんでした。進捗には影響ありません。',
    },
  },
  debrief: {
    replay: 'レベルをやり直す',
    level: (level: number) => `レベル${num(level, 'ja')}`,
    opening: '振り返りを開いています…',
    back: (level: number, title: string) => `← レベル${num(level, 'ja')} · ${title}`,
    next: (level: number) => `次へ · レベル${num(level, 'ja')}`,
    allCleared: 'すべてのレベルをクリア',
    getCourse: 'コースを購入',
    unplayed: {
      title: '先にプレイしてください。',
      body: (level: number) =>
        `振り返りでは、このレベルで使うすべてのコマンドと、それぞれがなぜ正しい選択だったのかを説明します。つまり、答えを詳しく書き下ろしたものです。レベルの前より後に読むほうが価値があるので、レベル${num(level, 'ja')}をクリアすると開きます。`,
      cta: (level: number) => `レベル${num(level, 'ja')}をプレイ`,
    },
    anonymous: {
      title: '読むにはログインしてください。',
      body: 'このレベルはコースの有料部分にあり、振り返りも同じです。振り返りはインターネット全体ではあなたのアカウントに向けて配信されるので、ログインするまでここには読めるものがありません。',
    },
    unowned: {
      title: 'このレベルは有料部分にあります。',
      body: (signedIn: boolean, from: number, count: number) =>
        `レベル${num(from, 'ja')}以降のすべてのレベルに、これと同じような振り返りが付いています。使ったコマンド、それぞれがなぜ正しい選択だったのか、そして仕事のどの場面から来ているのかをまとめたものです。${
          signedIn
            ? 'あなたのアカウントには、まだコースがありません。'
            : `全部で${num(count, 'ja')}レベル、つまり${num(count, 'ja')}本の振り返りがあります。`
        }`,
      bodyCount: (signedIn: boolean, count: number) =>
        `有料部分のすべてのレベルに、これと同じような振り返りが付いています。使ったコマンド、それぞれがなぜ正しい選択だったのか、そして仕事のどの場面から来ているのかをまとめたものです。${
          signedIn
            ? 'あなたのアカウントには、まだコースがありません。'
            : `全部で${num(count, 'ja')}レベル、つまり${num(count, 'ja')}本の振り返りがあります。`
        }`,
    },
    missing: {
      title: 'まだ書かれていません。',
      body: 'このレベルの振り返りはまだありません。それまでは、レベルの最後にある振り返りメモが要約の代わりです。',
    },
    unavailable: {
      title: '読み込めませんでした。',
      body: '振り返りはサーバーにあり、このページから接続できませんでした。進捗には影響ありません。',
    },
  },
};
